import type { ChangeEvent } from "react";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import type { InputOnChangeData } from "../../components/Input/Input";
import {
  type BoxListItem,
  type IWarehouseApi,
  QrBindingConflictError,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import type { IStore } from "../../stores/store";
import { STORE_TOKEN } from "../../stores/store";
import {
  SETTINGS_STORE_TOKEN,
  type ISettingsStore,
} from "../../stores/SettingsStore";
import {
  extractQrPayload,
  normalizeQrPayloadInput,
  qrScanErrorToMessage,
  validateQrPayload,
} from "../../domain/qr-payload";
import { normalizeTypeTags } from "../../utils/typeTags";
import { formatBoxReferenceLabel } from "../../utils/boxReference";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
} from "../../stores/navigation";
import { isSpeechRecognitionSupported } from "../../utils/speechRecognition";
import type { AddProductDraft } from "../AddProductPage/AddProductPageViewModel";
import { formatParentInputValue } from "./viewmodel/reference-data";
import { AddBoxVoiceController } from "./viewmodel/voice-controller";
import {
  applyBoxPatchOperation,
  clearAddBoxFormOperation,
  fillFromVoiceTranscriptOperation,
  goBackFromAddBoxOperation,
  loadAddBoxReferenceDataOperation,
  requestPhotoAutofillSuggestionOperation,
  setPhotoFromFileOperation,
  submitAddBoxOperation,
  type AddBoxPhotoAiSuggestion,
} from "./viewmodel/operations";
import { createAddBoxVoiceController } from "./viewmodel/voice-binding";

export interface AddBoxPageViewProps {
  returnToAddProduct?: boolean;
  productDraft?: AddProductDraft;
}

@injectable()
export class AddBoxPageViewModel extends ViewModel<
  unknown,
  AddBoxPageViewProps
> {
  @observable
  name = "";

  @observable
  description = "";

  @observable
  photo = "";

  @observable
  typeTags: string[] = [];

  @observable
  typeTagInput = "";

  @observable
  parentId = "";

  @observable.shallow
  availableBoxes: BoxListItem[] = [];

  @observable.shallow
  typeTagSuggestions: string[] = [];

  @observable
  isReferenceLoading = false;

  @observable
  referenceError: string | null = null;

  @observable
  isPhotoLoading = false;

  @observable
  isSubmitting = false;

  @observable
  error: string | null = null;

  @observable
  qrPayloadInput = "";

  @observable
  qrBindingError: string | null = null;

  @observable
  isQrScannerOpen = false;

  @observable
  isQrScanResolving = false;

  @observable
  voiceTranscript = "";

  @observable
  isVoicePanelOpen = false;

  @observable
  isVoiceListening = false;

  @observable
  isVoiceApplying = false;

  @observable
  voiceError: string | null = null;

  @observable
  voiceInfo: string | null = null;

  @observable
  isVoiceSupported = isSpeechRecognitionSupported();

  @observable.ref
  aiPhotoSuggestion: AddBoxPhotoAiSuggestion | null = null;

  private readonly returnToAddProduct: boolean;

  private readonly productDraftForReturn: AddProductDraft | null;

  private readonly voiceController: AddBoxVoiceController;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(SETTINGS_STORE_TOKEN)
    private readonly settingsStore: ISettingsStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    this.returnToAddProduct = this.viewProps?.returnToAddProduct === true;
    this.productDraftForReturn = this.viewProps?.productDraft
      ? {
          ...this.viewProps.productDraft,
          photos: [...this.viewProps.productDraft.photos],
          typeTags: [...this.viewProps.productDraft.typeTags],
        }
      : null;

    makeObservable(this);
    this.voiceController = createAddBoxVoiceController({ state: this });
  }

  @computed
  get title(): string {
    return `добавляем ${this.name.trim() ? this.name.trim() : "новую коробку"}`;
  }

  @computed
  get hasPhotoPreview(): boolean {
    return this.photo.trim().length > 0;
  }

  @computed
  get canSubmit(): boolean {
    return !this.isSubmitting;
  }

  @computed
  get backButtonLabel(): string {
    return this.returnToAddProduct
      ? "назад к созданию товара"
      : "назад к главной";
  }

  @computed
  get qrScannerDelay(): number | false {
    if (!this.isQrScannerOpen || this.isSubmitting || this.isQrScanResolving) {
      return false;
    }

    return 300;
  }

  @computed
  get canStartVoiceListening(): boolean {
    return (
      this.isVoiceSupported &&
      !this.isVoiceListening &&
      !this.isVoiceApplying &&
      !this.isSubmitting
    );
  }

  @computed
  get canStopVoiceListening(): boolean {
    return this.isVoiceListening;
  }

  @computed
  get canApplyVoiceTranscript(): boolean {
    return (
      this.isVoicePanelOpen &&
      !this.isVoiceApplying &&
      !this.isSubmitting &&
      this.voiceTranscript.trim().length >= 4
    );
  }

  @computed
  get canApplyPhotoAutofill(): boolean {
    return (
      this.settingsStore.aiPhotoAutofillEnabled &&
      !!this.photo.trim() &&
      !this.isVoiceApplying &&
      !this.isSubmitting
    );
  }

  @computed
  get isAiPhotoAutofillEnabled(): boolean {
    return this.settingsStore.aiPhotoAutofillEnabled;
  }

  @computed
  get isAiPhotoSuggestionOpen(): boolean {
    return !!this.aiPhotoSuggestion;
  }

  @computed
  get aiPhotoSuggestionDescription(): string {
    if (!this.aiPhotoSuggestion) {
      return "";
    }

    if (this.aiPhotoSuggestion.changes.length === 0) {
      return "AI не предложил изменений.";
    }

    const source = this.aiPhotoSuggestion.usedTranscriptContext
      ? "Источник: фото + текст диктовки"
      : "Источник: только фото";

    return [
      `Модель: ${this.aiPhotoSuggestion.model}`,
      source,
      "",
      "Предложенные изменения:",
      ...this.aiPhotoSuggestion.changes.map((line) => `- ${line}`),
    ].join("\n");
  }

  @computed
  get parentIdSuggestions(): Array<{ id: string; label: string }> {
    return this.availableBoxes.map((box) => ({
      id: box.id,
      label: formatBoxReferenceLabel(box),
    }));
  }

  protected onViewMounted(): void {
    this.aiPhotoSuggestion = null;
    void this.loadReferenceData();
  }

  protected onViewUnmounted(): void {
    this.voiceController.teardown();
  }

  @action
  onNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    this.name = data.value || event.target.value;
    this.error = null;
  };

  @action
  onDescriptionChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    this.description = data.value || event.target.value;
    this.error = null;
  };

  @action
  onPhotoChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    this.photo = data.value || event.target.value;
    this.error = null;
  };

  @action.bound
  async onPhotoFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    await setPhotoFromFileOperation({
      state: this,
      event,
    });
  }

  @action
  setTypeTagInput = (value: string): void => {
    this.typeTagInput = value;
    this.error = null;
  };

  @action.bound
  addTypeTags(tags: string[]): void {
    this.typeTags = normalizeTypeTags([...this.typeTags, ...tags]);
    this.error = null;
  }

  @action.bound
  removeTypeTag(tag: string): void {
    this.typeTags = this.typeTags.filter((item) => item !== tag);
    this.error = null;
  }

  @action
  onParentIdChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    this.parentId = data.value || event.target.value;
    this.error = null;
  };

  @action.bound
  toggleQrScanner(): void {
    if (this.isSubmitting) {
      return;
    }

    this.isQrScannerOpen = !this.isQrScannerOpen;
    this.qrBindingError = null;
  }

  @action.bound
  async onQrScan(rawData: string | null): Promise<void> {
    if (!rawData || !this.isQrScannerOpen || this.isQrScanResolving) {
      return;
    }

    const payload = extractQrPayload(rawData);
    if (!payload) {
      this.qrBindingError =
        "Сканируйте QR-код из партии печати (формат qr_...).";
      return;
    }

    runInAction(() => {
      this.isQrScanResolving = true;
      this.qrBindingError = null;
    });

    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (existing) {
        runInAction(() => {
          this.qrPayloadInput = "";
          this.qrBindingError = this.formatQrConflictMessage(
            payload,
            existing.entityId,
          );
        });
        return;
      }

      runInAction(() => {
        this.qrPayloadInput = payload;
        this.qrBindingError = null;
        this.isQrScannerOpen = false;
      });
    } catch (error) {
      runInAction(() => {
        this.qrBindingError =
          error instanceof Error
            ? error.message
            : "Не удалось проверить QR-код";
      });
    } finally {
      runInAction(() => {
        this.isQrScanResolving = false;
      });
    }
  }

  @action.bound
  onQrScanError(error: unknown): void {
    this.qrBindingError = qrScanErrorToMessage(error);
  }

  @action
  onVoiceTranscriptChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    this.voiceController.setManualTranscript(event.target.value);
  };

  @action.bound
  onVoicePrimaryButtonClick(): void {
    this.voiceController.onPrimaryButtonClick();
  }

  @action.bound
  closeVoicePanel(): void {
    this.voiceController.closePanel();
  }

  @action.bound
  clearVoiceTranscript(): void {
    this.voiceController.clearTranscript();
  }

  @action.bound
  startVoiceListening(): void {
    this.voiceController.startListening();
  }

  @action.bound
  stopVoiceListening(): void {
    this.voiceController.stopListening();
  }

  @action.bound
  async fillFromVoiceTranscript(): Promise<void> {
    await fillFromVoiceTranscriptOperation({
      state: this,
      api: this.api,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });
  }

  @action.bound
  async fillFromPhoto(): Promise<void> {
    if (!this.isAiPhotoAutofillEnabled) {
      this.voiceError = "AI-по-фото выключен в настройках приложения";
      return;
    }

    const suggestion = await requestPhotoAutofillSuggestionOperation({
      state: this,
      api: this.api,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });

    if (!suggestion) {
      return;
    }

    if (suggestion.changes.length === 0) {
      this.voiceInfo = "AI не предложил новых изменений по фото";
      return;
    }

    this.aiPhotoSuggestion = suggestion;
  }

  @action.bound
  applyAiPhotoSuggestion(): void {
    const suggestion = this.aiPhotoSuggestion;
    if (!suggestion) {
      return;
    }

    applyBoxPatchOperation({
      state: this,
      patch: suggestion.patch,
    });
    this.aiPhotoSuggestion = null;
    this.voiceInfo = suggestion.usedTranscriptContext
      ? `Черновик применен по фото + тексту (${suggestion.model})`
      : `Черновик применен по фото (${suggestion.model})`;
    this.voiceError = null;
  }

  @action.bound
  cancelAiPhotoSuggestion(): void {
    this.aiPhotoSuggestion = null;
    this.voiceInfo = "Предложенные AI-правки отклонены";
  }

  @action.bound
  async loadReferenceData(): Promise<void> {
    await loadAddBoxReferenceDataOperation({
      state: this,
      api: this.api,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });
  }

  @action
  clearForm = (): void => {
    clearAddBoxFormOperation({
      state: this,
      stopVoiceListening: () => this.stopVoiceListening(),
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });
    this.aiPhotoSuggestion = null;
    this.qrPayloadInput = "";
    this.qrBindingError = null;
    this.isQrScannerOpen = false;
    this.isQrScanResolving = false;
  };

  @action
  goBack = (): void => {
    goBackFromAddBoxOperation({
      returnToAddProduct: this.returnToAddProduct,
      productDraftForReturn: this.productDraftForReturn,
      navigationStore: this.navigationStore,
    });
  };

  @action
  submit = async (): Promise<void> => {
    const nextQrPayload = normalizeQrPayloadInput(this.qrPayloadInput);
    if (nextQrPayload && !validateQrPayload(nextQrPayload)) {
      this.qrBindingError = "Укажите QR в формате qr_...";
      return;
    }

    if (nextQrPayload) {
      const isQrAvailable = await this.ensureQrPayloadAvailable(nextQrPayload);
      if (!isQrAvailable) {
        return;
      }
    }

    this.qrBindingError = null;

    await submitAddBoxOperation({
      state: this,
      api: this.api,
      store: this.store,
      navigationStore: this.navigationStore,
      returnToAddProduct: this.returnToAddProduct,
      productDraftForReturn: this.productDraftForReturn,
      onCreated: (box) => {
        if (!nextQrPayload) {
          return;
        }

        return this.api
          .bindQrPayload({
            entityId: box.id,
            payload: nextQrPayload,
          })
          .then(() => undefined)
          .catch((error: unknown) => {
            if (error instanceof QrBindingConflictError) {
              this.qrBindingError = this.formatQrConflictMessage(
                nextQrPayload,
                error.existingEntityId,
              );
              return;
            }

            this.qrBindingError =
              error instanceof Error
                ? error.message
                : "Не удалось привязать QR-код к коробке";
          });
      },
    });
  };

  private async ensureQrPayloadAvailable(payload: string): Promise<boolean> {
    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (!existing) {
        return true;
      }

      this.qrBindingError = this.formatQrConflictMessage(
        payload,
        existing.entityId,
      );
      return false;
    } catch (error) {
      this.qrBindingError =
        error instanceof Error ? error.message : "Не удалось проверить QR-код";
      return false;
    }
  }

  private formatQrConflictMessage(
    payload: string,
    entityId: string | null,
  ): string {
    if (!entityId) {
      return `QR ${payload} уже используется другой сущностью.`;
    }

    return `QR ${payload} уже привязан к ${entityId}. Выберите другой код.`;
  }

  private formatParentInputValue(value: string): string {
    return formatParentInputValue(value, this.availableBoxes);
  }
}
